// Plain-language fallbacks (RCS-147): what a user sees when the server sent
// no usable message. Raw transport text such as "Network Error" or "Request
// failed with status code 500" never reaches the screen.
export const FRIENDLY_MESSAGES = {
    network: "We couldn't reach the server. Check your connection and try again.",
    timeout: "The server took too long to respond. Please try again.",
    400: "That request couldn't be processed. Check the details and try again.",
    401: "Your session has ended. Please sign in again.",
    403: "You don't have permission to do that. Ask your administrator if you need access.",
    404: "We couldn't find that record. It may have been moved or deleted.",
    409: "This record was changed by someone else. Refresh the page and try again.",
    413: "That file is too large to upload.",
    419: "Your session has expired. Refresh the page and try again.",
    422: "Some details need your attention. Check the highlighted fields and try again.",
    429: "Too many attempts. Wait a moment and try again.",
    500: "Something went wrong on our side. Please try again, or contact support if it keeps happening.",
    generic: "Something went wrong. Please try again.",
};

const TRANSPORT_TEXT = /^(Network Error|Request failed with status code \d+|timeout of \d+ms exceeded|An error occurred)$/i;

/** Error bodies of arraybuffer downloads arrive as bytes — read the JSON back out. */
function decodeBody(body) {
    const tag = Object.prototype.toString.call(body);
    if (tag === '[object ArrayBuffer]' || ArrayBuffer.isView(body)) {
        try {
            return JSON.parse(new TextDecoder().decode(body));
        } catch {
            return {};
        }
    }
    return body;
}

function fallbackFor(code, text) {
    if (/timeout/i.test(text || "")) return FRIENDLY_MESSAGES.timeout;
    if (/network/i.test(text || "")) return FRIENDLY_MESSAGES.network;
    const status = Number(code) || Number((text || "").match(/status code (\d+)/i)?.[1]);
    if (FRIENDLY_MESSAGES[status]) return FRIENDLY_MESSAGES[status];
    if (status >= 500) return FRIENDLY_MESSAGES[500];
    return FRIENDLY_MESSAGES.generic;
}

export class CustomAxiosError extends Error {
    constructor(response) {
        // API modules pass either the error body (`err.response?.data`) or,
        // when there was no response at all, axios' message string
        // (`err.message`, e.g. "Network Error"), which is swapped for a
        // plain sentence.
        if (typeof response === 'string') response = { message: response };
        response = decodeBody(response) || {};
        if (typeof response !== 'object') response = {};

        const raw = typeof response.message === 'string' ? response.message.trim() : '';
        super(raw && !TRANSPORT_TEXT.test(raw) ? raw : fallbackFor(response.code, raw));

        this.name = this.constructor.name;
        this.status = response.status || null;  // Status text (e.g. "Unprocessable Content")
        this.code = response.code || null;  // Numeric code (e.g. 422)
        this.data = response.data || null;
        this.errors = response.errors && typeof response.errors === 'object' ? response.errors : {};

        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, this.constructor);
        }
    }

    // Helper method to extract specific error messages from the validation errors
    getValidationErrors() {
        return getValidationErrors(this.errors);
    }
}

export const getValidationErrors = (errors) => {
    let validationErrors = [];
    for (const messages of Object.values(errors || {})) {
        validationErrors.push(`${[].concat(messages).join(', ')}`);
    }
    return validationErrors;
}

/**
 * The one sentence to show for any failure — a CustomAxiosError, a raw
 * axios error, a plain Error or a string — never transport jargon.
 */
export const friendlyMessage = (error, fallback = FRIENDLY_MESSAGES.generic) => {
    if (!error) return fallback;
    if (error instanceof CustomAxiosError) return error.message || fallback;
    if (error.isAxiosError || error.response) {
        if (!error.response) return fallbackFor(null, error.message);
        const body = decodeBody(error.response.data);
        return new CustomAxiosError(body && typeof body === 'object'
            ? { code: error.response.status, ...body }
            : { code: error.response.status }).message;
    }
    const text = typeof error === 'string' ? error : error.message;
    // Some callers reject with { message: { field: ["…"] } } — use the first sentence.
    if (text && typeof text === 'object') {
        const first = [].concat(Object.values(text)[0] ?? [])[0];
        return typeof first === 'string' && first.trim() ? first : fallback;
    }
    if (!text || TRANSPORT_TEXT.test(text)) return text ? fallbackFor(null, text) : fallback;
    return text;
};

/** Validation messages (422) from any error shape, as a flat list. */
export const validationMessages = (error) => {
    const errors = error?.errors || decodeBody(error?.response?.data)?.errors;
    return errors && typeof errors === 'object' ? getValidationErrors(errors) : [];
};
